import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Shield, 
  Clock, 
  Terminal, 
  Smartphone, 
  Monitor, 
  MapPin, 
  Search, 
  RefreshCw, 
  Info,
  CheckCircle2,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';

export default function AdminActivityLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('ALL');
  const [selectedLog, setSelectedLog] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const fetchLogs = () => {
    setLoading(true);
    api.get('/audit-logs?limit=100')
      .then((res) => {
        if (res?.success) setLogs(res.logs || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const parseDetails = (detailsStr) => {
    if (!detailsStr) return null;
    try {
      return typeof detailsStr === 'string' ? JSON.parse(detailsStr) : detailsStr;
    } catch {
      return detailsStr;
    }
  };

  const formatAction = (action) => {
    switch (action) {
      case 'STAFF_LOGIN': return { label: 'Admin kirdi', color: 'bg-emerald-50 text-emerald-800 border-emerald-300' };
      case 'CLIENT_LOGIN': return { label: 'Mijoz profiliga kirdi', color: 'bg-blue-50 text-blue-800 border-blue-300' };
      case 'LOGIN_FAILED': return { label: 'Parol xato terildi', color: 'bg-rose-50 text-rose-800 border-rose-300' };
      case 'BOOKING_CREATED': return { label: 'Yangi ariza kelib tushdi', color: 'bg-amber-50 text-amber-800 border-amber-300' };
      case 'APPOINTMENT_CONFIRMED': return { label: 'Ariza tasdiqlandi', color: 'bg-emerald-50 text-emerald-800 border-emerald-300' };
      case 'APPOINTMENT_CANCELLED': return { label: 'Ariza bekor qilindi', color: 'bg-rose-50 text-rose-800 border-rose-300' };
      case 'APPOINTMENT_REJECTED': return { label: 'Ariza rad etildi', color: 'bg-rose-50 text-rose-800 border-rose-300' };
      case 'APPOINTMENT_EDITED': return { label: 'Ariza ma’lumotlari tahrirlandi', color: 'bg-indigo-50 text-indigo-800 border-indigo-300' };
      case 'APPOINTMENT_COMPLETED': return { label: 'Uchrashuv muvaffaqiyatli o‘tkazildi', color: 'bg-emerald-50 text-emerald-800 border-emerald-300' };
      case 'OFFICE_CREATED': return { label: 'Yangi filial qo‘shildi', color: 'bg-sky-50 text-sky-800 border-sky-300' };
      case 'OFFICE_UPDATED': return { label: 'Filial manzili tahrirlandi', color: 'bg-sky-50 text-sky-800 border-sky-300' };
      case 'OFFICE_DELETED': return { label: 'Filial o‘chirildi', color: 'bg-rose-50 text-rose-800 border-rose-300' };
      case 'OFFICE_DEACTIVATED': return { label: 'Filial arxivlandi', color: 'bg-amber-50 text-amber-800 border-amber-300' };
      case 'LAWYER_CREATED': return { label: 'Yangi advokat qo‘shildi', color: 'bg-violet-50 text-violet-800 border-violet-300' };
      case 'LAWYER_UPDATED': return { label: 'Advokat ma’lumotlari yangilandi', color: 'bg-violet-50 text-violet-800 border-violet-300' };
      case 'LAWYER_NOTE_CREATED': return { label: 'Sud kundaligiga qayd yozildi', color: 'bg-slate-100 text-slate-800 border-slate-300' };
      case 'LAWYER_NOTE_UPDATED': return { label: 'Sud qaydnomasi yangilandi', color: 'bg-slate-100 text-slate-800 border-slate-300' };
      case 'LAWYER_NOTE_DELETED': return { label: 'Sud qaydnomasi o‘chirildi', color: 'bg-rose-50 text-rose-800 border-rose-300' };
      case 'LAWYER_SCHEDULE_OVERRIDE_SET': return { label: 'Advokat ish grafigi band qilindi', color: 'bg-amber-50 text-amber-800 border-amber-300' };
      case 'REVIEW_CREATED': return { label: 'Mijoz fikri qo‘shildi', color: 'bg-teal-50 text-teal-800 border-teal-300' };
      case 'REVIEW_UPDATED': return { label: 'Mijoz fikri tahrirlandi', color: 'bg-teal-50 text-teal-800 border-teal-300' };
      default: return { label: action.replace(/_/g, ' '), color: 'bg-slate-100 text-slate-800 border-slate-200' };
    }
  };

  const formatDevice = (device) => {
    if (!device) return 'Kompyuter (Windows / Chrome)';
    return device
      .replace(/Desktop/i, 'Kompyuter')
      .replace(/Mobile/i, 'Telefon')
      .replace(/Unknown device/i, 'Noma’lum qurilma');
  };

  const formatDetails = (detailsObj) => {
    if (!detailsObj) return '—';
    if (typeof detailsObj === 'string') return detailsObj;
    const parts = [];
    if (detailsObj.status) parts.push(detailsObj.status);
    if (detailsObj.reason) parts.push(detailsObj.reason);
    if (detailsObj.loginInput) parts.push(`Login: ${detailsObj.loginInput}`);
    if (detailsObj.phone) parts.push(`Tel: ${detailsObj.phone}`);
    if (detailsObj.attemptedLogin) parts.push(`Kiritilgan: ${detailsObj.attemptedLogin}`);
    if (detailsObj.previousStatus && detailsObj.newStatus) {
      parts.push(`${detailsObj.previousStatus} ➔ ${detailsObj.newStatus}`);
    }
    return parts.length > 0 ? parts.join(' | ') : JSON.stringify(detailsObj);
  };

  const filteredLogs = logs.filter((log) => {
    if (filterAction !== 'ALL') {
      if (filterAction === 'LOGIN' && !log.action?.includes('LOGIN')) return false;
      if (filterAction === 'APPOINTMENT' && !log.action?.includes('APPOINTMENT')) return false;
      if (filterAction === 'OFFICE' && !log.action?.includes('OFFICE')) return false;
      if (filterAction === 'LAWYER' && !log.action?.includes('LAWYER')) return false;
    }

    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const detailsText = typeof log.details === 'string' ? log.details : JSON.stringify(log.details || '');
    return (
      log.userName?.toLowerCase().includes(q) ||
      log.action?.toLowerCase().includes(q) ||
      log.deviceInfo?.toLowerCase().includes(q) ||
      log.location?.toLowerCase().includes(q) ||
      log.ipAddress?.toLowerCase().includes(q) ||
      detailsText.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-lg font-bold text-slate-900">
            Xavfsizlik & Harakatlar Tarixi (Audit & Device Logs)
          </h2>
          <p className="text-xs text-slate-500">
            Tizimga kirgan shaxslar, qurilma/brauzer modeli, IP manzili, geografik lokatsiya va operatsiyalar katalogi
          </p>
        </div>

        <button
          onClick={fetchLogs}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded shadow-2xs transition-colors cursor-pointer shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Yangilash
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Foydalanuvchi, harakat, IP, qurilma yoki shahar bo‘yicha izlash..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-xs bg-white border border-slate-300 rounded focus:border-[#c5a880] focus:outline-none"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex space-x-1.5 overflow-x-auto shrink-0 text-xs">
          {[
            { id: 'ALL', label: 'Barchasi' },
            { id: 'LOGIN', label: 'Tizimga kirishlar' },
            { id: 'APPOINTMENT', label: 'Arizalar' },
            { id: 'OFFICE', label: 'Ofislar' },
            { id: 'LAWYER', label: 'Advokatlar' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilterAction(tab.id)}
              className={`px-3 py-1.5 rounded font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                filterAction === tab.id
                  ? 'bg-[#091322] text-[#c5a880]'
                  : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-xl border border-slate-300 shadow-xs overflow-hidden">
        {/* Top summary & scroll indicator bar */}
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs text-slate-600 font-medium">
          <div className="flex items-center space-x-2">
            <span className="font-bold text-slate-800">Qaydnomalar jurnali</span>
            <span className="text-slate-400">({filteredLogs.length} ta yozuv)</span>
          </div>
          <span className="text-[11px] text-slate-500 font-normal hidden sm:inline-flex items-center">
            Barcha ustunlar va amallarni ko‘rish uchun o‘ng tomonga aylantiring &rarr;
          </span>
        </div>

        {/* Table container with fixed max height, clear borders and high-visibility scrollbar */}
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto admin-table-scroll border-b border-slate-200">
          <table className="w-full text-left text-xs min-w-[960px] border-collapse">
            <thead className="bg-slate-100/90 text-slate-700 border-b-2 border-slate-200 uppercase tracking-wider text-[10px] sticky top-0 z-10 shadow-2xs font-bold">
              <tr>
                <th className="py-3 px-4 border-r border-slate-200/60">Vaqt</th>
                <th className="py-3 px-4 border-r border-slate-200/60">Foydalanuvchi</th>
                <th className="py-3 px-4 border-r border-slate-200/60">Amal (Harakat turi)</th>
                <th className="py-3 px-4 border-r border-slate-200/60">Qurilma & Brauzer</th>
                <th className="py-3 px-4 border-r border-slate-200/60">Hudud (Shahar)</th>
                <th className="py-3 px-4 border-r border-slate-200/60">Bajarilgan amal tafsiloti</th>
                <th className="py-3 px-4 border-r border-slate-200/60">IP Manzil</th>
                <th className="py-3 px-4 text-right">Batafsil</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/80">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">Hech qanday log topilmadi</td>
                </tr>
              ) : (
                filteredLogs.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((log) => {
                  const detailsObj = parseDetails(log.details);
                  const actionMeta = formatAction(log.action);

                  return (
                    <tr key={log.id} className="hover:bg-slate-50 text-[11px] transition-colors">
                      <td className="py-3 px-4 text-slate-500 font-mono whitespace-nowrap border-r border-slate-100">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-900 whitespace-nowrap border-r border-slate-100">
                        {log.userName}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap border-r border-slate-100">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded font-semibold text-[10px] border shadow-2xs ${actionMeta.color}`}>
                          {actionMeta.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap border-r border-slate-100">
                        <div className="flex items-center space-x-1.5" title={log.deviceInfo}>
                          {log.deviceInfo?.includes('Mobil') || log.deviceInfo?.includes('Phone') || log.deviceInfo?.includes('Android') || log.deviceInfo?.includes('iOS') ? (
                            <Smartphone className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          ) : (
                            <Monitor className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          )}
                          <span className="truncate max-w-[150px] font-medium">{formatDevice(log.deviceInfo)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap border-r border-slate-100">
                        <div className="flex items-center space-x-1">
                          <MapPin className="w-3 h-3 text-[#c5a880] shrink-0" />
                          <span className="truncate max-w-[140px]">{log.location || 'Jizzax, O‘zbekiston'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-[11px] max-w-sm truncate border-r border-slate-100" title={formatDetails(detailsObj)}>
                        {formatDetails(detailsObj)}
                      </td>
                      <td className="py-3 px-4 text-slate-500 font-mono whitespace-nowrap border-r border-slate-100">
                        {log.ipAddress || '127.0.0.1'}
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="p-1 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded cursor-pointer"
                          title="To‘liq tafsilotlarni ko‘rish"
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Summary Bar (Always Visible at Card Bottom) */}
        {filteredLogs.length > 0 && (
          <div className="p-3.5 border-t border-slate-200 bg-slate-50/70 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-600">
            <div>
              Jami: <strong>{filteredLogs.length}</strong> ta harakat qaydnomasi (Sahifa {currentPage} / {Math.ceil(filteredLogs.length / pageSize) || 1})
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 bg-white border border-slate-300 rounded font-medium disabled:opacity-50 hover:bg-slate-100 cursor-pointer"
              >
                ← Oldingi
              </button>
              <span className="font-semibold text-slate-800 px-1">
                {currentPage}
              </span>
              <button
                type="button"
                disabled={currentPage >= Math.ceil(filteredLogs.length / pageSize)}
                onClick={() => setCurrentPage((p) => Math.min(Math.ceil(filteredLogs.length / pageSize), p + 1))}
                className="px-3 py-1 bg-white border border-slate-300 rounded font-medium disabled:opacity-50 hover:bg-slate-100 cursor-pointer"
              >
                Keyingi →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {selectedLog && (
        <Modal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title={`Audit Log Tafsilotlari: #${selectedLog.id}`}
          maxWidth="max-w-lg"
        >
          <div className="space-y-4 text-xs">
            <div className="bg-slate-50 border border-slate-200 rounded p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Amal (Action):</span>
                <span className="font-mono font-bold text-slate-800">{selectedLog.action}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Foydalanuvchi:</span>
                <span className="font-semibold text-slate-800">{selectedLog.userName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Sana & Vaqt:</span>
                <span className="font-mono text-slate-700">{new Date(selectedLog.createdAt).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Qurilma / OS / Brauzer:</span>
                <span className="font-medium text-slate-800">{selectedLog.deviceInfo || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Geografik Hudud:</span>
                <span className="font-medium text-slate-800">{selectedLog.location || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">IP Manzili:</span>
                <span className="font-mono text-slate-700">{selectedLog.ipAddress || '127.0.0.1'}</span>
              </div>
            </div>

            <div>
              <span className="block font-semibold uppercase text-slate-700 mb-1">To‘liq Parametrlar & JSON Telemetriya:</span>
              <pre className="p-3 bg-slate-900 text-amber-300 rounded font-mono text-[11px] overflow-x-auto leading-relaxed">
                {JSON.stringify(parseDetails(selectedLog.details) || selectedLog.details, null, 2)}
              </pre>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 bg-[#091322] text-[#c5a880] rounded font-semibold cursor-pointer"
              >
                Yopish
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
